import React from "react";
import { styled } from "@mui/system";
import CrossIcon from "src/icons/CrossIcon";
import { iconSelector } from "../transactions/helperfns";
import { Link as RouterLink } from "react-router-dom";
import { NotificationRes } from "src/store/features/api/generated";

const Wrapper = styled("div")(() => ({
  display: "flex",
  width: "100%",
  padding: "8px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: " flex-start",
  gap: "12px",
  background: "#FFF",

  "&:hover": {
    background: "#F8F9F9",
  },

  // handles CloseIconWrapper styled component
  "&:hover > div > div:last-child": {
    visibility: "visible",
  },
}));

const Text = styled("div")(() => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

const Content = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
  justifyContent: "space-between",
}));

const Link = styled(RouterLink)(() => ({
  color: "#FC4C4C",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
  textDecorationLine: "underline",
}));

const IconAndTextWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
}));

const IconWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "flex-start",
  height: "32px",
  width: "32px",
}));

const CloseIconWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
  cursor: "pointer",
  visibility: "hidden",
}));

interface NotificationItemProps {
  notification: NotificationRes;
  onRemove?: (notification: NotificationRes) => void;
}

/**
 * A component representing an individual notification item.
 * @param {INotification} props.notification - The notification data.
 * @param {INotification} props.onClick - The function to run when click on the X icon.
 */
const NotificationItem = (props: NotificationItemProps) => {
  const { notification, onRemove } = props;

  // Replace link placeholder with actual link
  const message = React.useMemo(() => {
    // if no link, return content as is
    if (!notification.relatedId) return notification.message;

    let notifLink = "";
    switch (notification.type) {
      case "TRANSACTION":
        notifLink = `/transactions/overview?ct=${notification.relatedId}`;
        break;
      case "CONTACT_CHANGE":
        notifLink = `/settings/profile`;
        break;
      default:
        break;
    }

    if (notification.message.includes("resume your contact change request")) {
      const messageParts = notification.message.split("here");
      return (
        <div>
          {messageParts[0]} {/* Text before the link */}
          <Link to={notifLink}>here</Link> {/* Link */}
          {messageParts[1]} {/* Text after the link */}
        </div>
      );
    } else if (notification.message.includes("{{here}}")) {
      const messageParts = notification.message.split("{{here}}");
      return (
        <div>
          {messageParts[0]} {/* Text before the link */}
          <Link to={notifLink}>here</Link> {/* Link */}
          {messageParts[1]} {/* Text after the link */}
        </div>
      );
    }
    return notification.message;
  }, []);

  const handleReadNotification = (notification: NotificationRes) => {
    if (onRemove) onRemove(notification);
  };

  return (
    <Wrapper>
      <Content>
        <IconAndTextWrapper>
          <IconWrapper>
            {iconSelector(notification.subType ?? "", notification.type, notification.message)}
          </IconWrapper>
          <Text>{message}</Text>
        </IconAndTextWrapper>
        <CloseIconWrapper onClick={() => handleReadNotification(notification)}>
          <CrossIcon height="12px" width="12px" />
        </CloseIconWrapper>
      </Content>
    </Wrapper>
  );
};

export default NotificationItem;
